import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { studioApi } from '@/api/studio';
import { Dialog } from '@/components/ui';
import { openedValues } from './openedValues';

/** The value of an access sent to me, in a window: opened once, the server erases it on opening. */
export function OpenOnce({ id, title, from }: { id: string; title: string; from: string }) {
  const qc = useQueryClient();
  const [value, setValue] = useState<string | undefined>(() => openedValues.get(id));
  const [show, setShow] = useState(() => openedValues.has(id));
  const [copied, setCopied] = useState(false);
  // Not the invalidating mutation: the lists refresh when the window closes, not under it.
  const open = useMutation({
    mutationFn: () => studioApi.openSecret(id),
    onSuccess: (v) => {
      openedValues.set(id, v);
      setValue(v);
      setShow(true);
    },
    meta: { inline: true },
  });
  const close = () => {
    setShow(false);
    qc.invalidateQueries({ queryKey: ['studio'] });
  };
  return (
    <div className="mt-2 flex flex-wrap items-center gap-3">
      <button className="btn-primary h-7 px-2.5 text-xs" disabled={open.isPending} onClick={() => (value !== undefined ? setShow(true) : open.mutate())}>
        {open.isPending ? 'Расшифровываем…' : value !== undefined ? 'Показать ещё раз' : 'Открыть один раз'}
      </button>
      {open.error && (
        <span role="alert" className="text-sm text-overdue">
          {(open.error as Error).message}
        </span>
      )}
      <Dialog open={show && value !== undefined} onClose={close} title={`Доступ: ${title}`}>
        <div className="space-y-3 p-5">
          <p className="text-xs text-muted">От {from}</p>
          <pre className="whitespace-pre-wrap break-all rounded bg-sunken p-3 font-mono text-sm">{value}</pre>
          <p className="text-xs text-muted">Сохраните сейчас: на сервере значение уже стёрто, после перезагрузки страницы оно не откроется.</p>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={async () => {
                await navigator.clipboard.writeText(value ?? '').catch(() => undefined);
                setCopied(true);
              }}
            >
              {copied ? 'Скопировано' : 'Скопировать'}
            </button>
            <button type="button" className="btn-primary" onClick={close}>
              Готово
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
